// ACTIVIDAD RECIENTE — feed unificado (ALTA / MEDIA / BAJA): (a) eventos BAJA que NO generaron
// alarma y (b) casos MEDIA/ALTA ya CERRADOS que salieron de la cola. Se muestra en la columna
// izquierda de Central V3, entre Alarmas de persona e Incidencias técnicas. Es informativa: sin
// sonido, sin Global Alert, sin tab rojo, sin ownership. Al hacer click en CUALQUIER item se abre
// SIEMPRE el mismo "Detalle del evento" (SOLO LECTURA) con los datos REALES del item (prioridad,
// recepción, estado y si generó alarma/caso). NO enruta a CaseDetailV3, NO enruta por prioridad.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Activity, ArrowRight, X, MapPin, Clock, Radio, ShieldCheck } from "lucide-react";
import { verboEvento, iconoEvento } from "@/lib/consolaV3/meta";
import { fmtTimestamp } from "@/lib/format";

const hhmm = (iso) => {
  try {
    return new Intl.DateTimeFormat("es-MX", { timeZone: "America/Tijuana", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(iso));
  } catch { return ""; }
};

const PRIO_LABEL = { alta: "Alta", media: "Media", baja: "Baja" };
const CLASIF_LABEL = {
  verificado_directamente: "Verificado directamente",
  verificado_contacto_autorizado: "Verificado por contacto autorizado",
  emergencia_handoff_completado: "Handoff a emergencias completado",
  no_verificado_accion_final_cumplida: "No verificado — acción final cumplida",
  recordatorio_comunicado: "Recordatorio comunicado",
  sin_contacto: "Sin contacto",
};
const prioTheme = (p) => {
  if (p === "alta") return { badge: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300", box: "border-red-400/40 bg-red-500/[0.06]", icon: "text-red-600 dark:text-red-300" };
  if (p === "media") return { badge: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300", box: "border-amber-400/40 bg-amber-500/[0.06]", icon: "text-amber-600 dark:text-amber-300" };
  return { badge: "bg-[#01CBE3]/20 text-[#0891A6] dark:text-[#01CBE3]", box: "border-[#01CBE3]/30 bg-[#01CBE3]/[0.06]", icon: "text-[#0891A6] dark:text-[#01CBE3]" };
};

// Drawer ÚNICO para todo item de Actividad reciente. Renderiza los datos REALES del item:
// prioridad (ALTA/MEDIA/BAJA), recepción real, estado real y si generó alarma/caso. Sólo cuando
// el item NO generó alarma (evento BAJA) se muestra "no generó alarma".
function DetalleDrawer({ ev, onClose, onBitacora }) {
  const Icon = iconoEvento(ev.tipo_evento);
  const prio = ev.prioridad || null;
  const prioLabel = prio ? (PRIO_LABEL[prio] || "Baja") : null;
  const esCaso = ev.origen === "caso";
  // Existencia de caso: la publica el read-model. Un evento asociado a un caso NO «no generó alarma».
  const tieneCaso = !!(ev.alarma_id || ev.caso_id);
  const t = prioTheme(prio);
  const clasifLabel = CLASIF_LABEL[ev.clasificacion] || null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="v3-actividad-detalle">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 h-full w-full max-w-[460px] overflow-y-auto bg-[#F1F5F9] shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
          <p className="text-sm font-bold text-[#0891A6] dark:text-[#01CBE3]">Detalle del evento</p>
          <button data-testid="v3-actividad-detalle-close" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4 space-y-3.5">
          <div className={`rounded-xl border-2 p-5 ${t.box}`}>
            <span data-testid="v3-actividad-detalle-badge" className={`inline-flex items-center rounded px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${t.badge}`}>
              {esCaso ? "Alarma" : "Evento"}{prioLabel ? ` · ${prioLabel}` : ""}
            </span>
            <div className="mt-2 flex items-start gap-3">
              <Icon strokeWidth={1.9} className={`h-8 w-8 shrink-0 ${t.icon}`} />
              <div>
                <h1 className="text-xl font-extrabold tracking-tight text-[#012472]">{verboEvento(ev.tipo_evento)}</h1>
                {ev.persona_nombre && <p className="mt-1 text-base font-bold text-slate-800 dark:text-slate-200">{ev.persona_nombre}</p>}
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2 text-sm">
            {(ev.sitio_nombre || ev.zona_nombre) && (
              <p className="flex items-center gap-2 text-slate-600 dark:text-slate-300"><MapPin className="h-4 w-4 text-slate-400" /> {[ev.sitio_nombre, ev.zona_nombre].filter(Boolean).join(" · ")}</p>
            )}
            <p data-testid="v3-actividad-detalle-recibido" className="flex items-center gap-2 text-slate-600 dark:text-slate-300"><Clock className="h-4 w-4 text-slate-400" /> Recibido {fmtTimestamp(ev.hora_recepcion_servidor)}</p>
            {esCaso && ev.cierre_en && (
              <p data-testid="v3-actividad-detalle-cerrado" className="flex items-center gap-2 text-slate-600 dark:text-slate-300"><Clock className="h-4 w-4 text-slate-400" /> Cerrado {fmtTimestamp(ev.cierre_en)}</p>
            )}
            {ev.dispositivo_nombre && <p className="flex items-center gap-2 text-slate-500 dark:text-slate-400"><Radio className="h-4 w-4 text-slate-400" /> {ev.dispositivo_nombre}</p>}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {prioLabel && <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-bold uppercase ${t.badge}`}>Prioridad {prioLabel}</span>}
              {ev.estado_label && <span data-testid="v3-actividad-detalle-estado" className="inline-flex items-center gap-1 rounded-md bg-slate-100 dark:bg-slate-700 px-2 py-0.5 text-[11px] font-bold uppercase text-slate-500 dark:text-slate-300">{ev.estado_label}</span>}
            </div>
          </div>

          <div className={`rounded-xl border p-4 space-y-1.5 bg-white ${esCaso ? "border-emerald-500/30" : "border-[#01CBE3]/20"}`}>
            {esCaso ? (
              <>
                <p data-testid="v3-actividad-detalle-alarma" className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200"><ShieldCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400" /> Sí generó una alarma · atendida y cerrada.</p>
                {clasifLabel && <p className="text-xs text-slate-500 dark:text-slate-400">Desenlace: {clasifLabel}.</p>}
              </>
            ) : tieneCaso ? (
              <>
                <p data-testid="v3-actividad-detalle-alarma" className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200"><ShieldCheck className="h-4 w-4 text-[#0891A6] dark:text-[#01CBE3]" /> Este evento está asociado a un caso.</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Su atención y su reporte se consultan en el caso, no aquí.</p>
              </>
            ) : (
              <>
                <p data-testid="v3-actividad-detalle-alarma" className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200"><ShieldCheck className="h-4 w-4 text-[#0891A6] dark:text-[#01CBE3]" /> Este evento fue monitoreado y no generó alarma.</p>
                {prio === "baja" && !ev.fuente && <p className="text-xs text-slate-500 dark:text-slate-400">Política efectiva: BAJA · sin escalamiento.</p>}
              </>
            )}
          </div>

          <button data-testid="v3-actividad-detalle-bitacora" onClick={onBitacora}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#012472]/5 dark:bg-[#01CBE3]/10 px-3 py-2 text-xs font-semibold text-[#012472] dark:text-[#01CBE3] hover:bg-[#012472]/10 transition-colors">
            Ver Bitácora <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ActividadReciente({ eventos = [] }) {
  const navigate = useNavigate();
  const { isExternal } = useAuth();
  const [sel, setSel] = useState(null);
  const items = (eventos || []).slice(0, 5);

  // v2.19.8 — Gate D cerró el panel ENTERO para cuentas institucionales porque la Bitácora interna
  // es internal-only. Ya existe `/bitacora-operativa`, autorizada y con alcance, así que el panel
  // se restituye y el enlace lleva a la bitácora que cada cuenta sí puede consultar.
  const rutaBitacora = isExternal ? "/bitacora-operativa" : "/bitacora";

  // VIS-01 · la sección era `bg-white/50` y en noche se resolvía a rgba(255,255,255,.5) =
  // superficie CLARA con tarjetas navy encima. Ahora la sección es la superficie de sección
  // (nivel 1, muy discreta) y las tarjetas la superficie elevada (nivel 2), con borde casi
  // imperceptible: menos ruido, el evento y su hora se leen primero.
  return (
    <div data-testid="v3-actividad-reciente" className="shrink-0 border-t border-slate-200 bg-slate-50/70 max-h-[30%] overflow-y-auto p-3 space-y-2">
      <div className="flex items-center justify-between px-0.5">
        <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#0891A6] dark:text-[#01CBE3]">
          <Activity className="h-3.5 w-3.5" /> Actividad reciente
        </p>
        <button data-testid="v3-actividad-ver-bitacora" onClick={() => navigate(rutaBitacora)}
          className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-slate-400 hover:text-[#0891A6] dark:hover:text-[#01CBE3] transition-colors">
          Ver Bitácora <ArrowRight className="h-3 w-3" />
        </button>
      </div>

      {items.length === 0 ? (
        <p data-testid="v3-actividad-vacia" className="py-3 text-center text-xs text-slate-400 dark:text-slate-600">Sin actividad reciente</p>
      ) : (
        items.map((e) => {
          const Icon = iconoEvento(e.tipo_evento);
          return (
            <button key={e.id} data-testid={`v3-actividad-item-${e.id}`} onClick={() => setSel(e)}
              className="flex w-full items-center gap-2.5 rounded-lg border border-slate-100 bg-white px-2.5 py-2.5 text-left transition-colors hover:border-[#01CBE3]/60">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#01CBE3]/10 text-[#0891A6] dark:text-[#01CBE3]">
                <Icon className="h-4 w-4" strokeWidth={1.8} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-bold text-slate-800">{verboEvento(e.tipo_evento)}</p>
                <p className="truncate text-[11px] text-slate-500">{[e.persona_nombre, e.zona_nombre].filter(Boolean).join(" · ") || "—"}</p>
              </div>
              <div className="shrink-0 text-right">
                <span className={`block rounded px-1.5 py-0.5 text-[9px] font-bold uppercase ${
                  e.prioridad === "alta" ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                  : e.prioridad === "media" ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                  : "bg-[#01CBE3]/15 text-[#0891A6] dark:text-[#01CBE3]"}`}>{e.prioridad || "sin prioridad"}</span>
                {e.estado_label && <span data-testid={`v3-actividad-estado-${e.id}`} className="mt-0.5 block rounded bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 text-[9px] font-bold uppercase text-slate-500 dark:text-slate-300">{e.estado_label}</span>}
                <span className="mt-0.5 block text-[10px] tabular-nums text-slate-400">{hhmm(e.hora_recepcion_servidor)}</span>
              </div>
            </button>
          );
        })
      )}

      {/* NAV-001: si el evento ya generó un caso, la Bitácora se abre CON ese caso (id técnico,
          sin PII en la URL) para no perder el contexto al cambiar de pantalla. La cuenta
          institucional abre SU bitácora, acotada al residente cuando ese dato consta. */}
      {sel && <DetalleDrawer ev={sel} onClose={() => setSel(null)}
        onBitacora={() => {
          const id = sel.alarma_id; const pid = sel.persona_id;
          setSel(null);
          if (isExternal) navigate(pid ? `/bitacora-operativa?persona_id=${encodeURIComponent(pid)}` : rutaBitacora);
          else navigate(id ? `/bitacora?caso_id=${id}` : rutaBitacora);
        }} />}
    </div>
  );
}
